<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import LinkedTasksPreview from 'dashboard/components-next/Tasks/LinkedTasksPreview.vue';
import TaskForm from 'dashboard/components-next/Tasks/TaskForm.vue';
import { getPreferredLanguageName } from 'dashboard/components-next/Contacts/preferredLanguages';
import { formatPreferenceValue } from 'dashboard/helper/contactPreferences';
import CloseCircleLinkedProjects from './CloseCircleLinkedProjects.vue';
import { useCloseCircleState } from './useCloseCircleState.js';

const props = defineProps({
  member: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    default: null,
  },
  projects: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'back',
  'edit',
  'addImportantDate',
  'detach',
  'createProject',
  'openProject',
]);

const { t, locale } = useI18n();
const closeCircle = useCloseCircleState();
const taskFormRef = ref(null);
const showActionsMenu = ref(false);

const memberName = computed(() => closeCircle.memberName(props.member));

const relationshipLabel = computed(() =>
  t(closeCircle.relationshipConfig(props.member.relationship).labelKey)
);

const contactId = computed(() => props.contact?.id ?? props.member.contactId);

const linkedProjects = computed(() =>
  props.projects.filter(
    project =>
      project.beneficiaryType === 'close_circle_member' &&
      String(project.beneficiaryId) === String(props.member.id)
  )
);

const preferredLanguageName = computed(() =>
  getPreferredLanguageName(props.member.preferredLanguage)
);

const hasInformation = computed(
  () =>
    Boolean(props.member.phone) ||
    Boolean(props.member.email) ||
    Boolean(preferredLanguageName.value) ||
    Boolean(props.member.canBeContactedDirectly)
);

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));
};

const nextOccurrence = value => {
  const date = new Date(value);
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const occurrence = new Date(
    today.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  if (occurrence < todayStart) {
    occurrence.setFullYear(today.getFullYear() + 1);
  }
  return occurrence.getTime();
};

const importantDateRows = computed(() =>
  (props.member.importantDates ?? [])
    .filter(date => date.date)
    .map(date => ({
      ...date,
      label: date.label?.trim() ?? '',
      typeLabel: t(closeCircle.dateTypeConfig(date.type).labelKey),
      formattedDate: formatDate(date.date),
      sortValue: nextOccurrence(date.date),
    }))
    .sort((a, b) => a.sortValue - b.sortValue)
);

const preferenceRows = computed(() => {
  const contactPreferences = props.member.contactPreferences || [];
  return contactPreferences
    .map(preference => ({
      label: preference.label,
      value: formatPreferenceValue(preference.value),
    }))
    .filter(row => row.value);
});

const hasPersonalMarkers = computed(
  () =>
    Boolean(importantDateRows.value.length) ||
    Boolean(preferenceRows.value.length) ||
    Boolean(props.member.notes)
);

const createTask = () => {
  showActionsMenu.value = false;
  const project = linkedProjects.value[0];
  taskFormRef.value?.open({
    title: t('CLOSE_CIRCLE.TASKS.DEFAULT_TITLE', {
      contact: props.contact?.name ?? '',
      member: props.member.firstName,
    }),
    description: t('CLOSE_CIRCLE.TASKS.DEFAULT_DESCRIPTION', {
      member: memberName.value,
      relationship: relationshipLabel.value,
    }),
    linkedContactId: contactId.value,
    linkedProjectId: project?.id ?? null,
    linkedCloseCircleMemberId: props.member.id,
    closeCircleMemberName: memberName.value,
  });
};

const editMember = () => {
  showActionsMenu.value = false;
  emit('edit', props.member);
};

const addImportantDate = () => {
  showActionsMenu.value = false;
  emit('addImportantDate', props.member);
};

const createProject = () => {
  showActionsMenu.value = false;
  emit('createProject', props.member);
};

const detachMember = () => {
  showActionsMenu.value = false;
  emit('detach', props.member);
};

const PRIMARY_ACTIONS = [
  {
    key: 'edit',
    icon: 'i-lucide-pencil',
    labelKey: 'CLOSE_CIRCLE.ACTIONS.EDIT',
    handler: editMember,
  },
  {
    key: 'add_important_date',
    icon: 'i-lucide-calendar-plus',
    labelKey: 'CLOSE_CIRCLE.ACTIONS.ADD_IMPORTANT_DATE',
    handler: addImportantDate,
  },
  {
    key: 'create_project',
    icon: 'i-lucide-gift',
    labelKey: 'CLOSE_CIRCLE.ACTIONS.CREATE_PROJECT',
    handler: createProject,
  },
  {
    key: 'create_task',
    icon: 'i-lucide-list-plus',
    labelKey: 'CLOSE_CIRCLE.ACTIONS.CREATE_TASK',
    handler: createTask,
  },
];

const MENU_ACTIONS = [
  {
    key: 'detach',
    icon: 'i-lucide-unlink',
    labelKey: 'CLOSE_CIRCLE.ACTIONS.DETACH',
    handler: detachMember,
    color: 'ruby',
  },
];
</script>

<template>
  <div class="flex min-h-0 w-full flex-col">
    <div
      class="sticky top-0 z-10 flex flex-col gap-3 bg-n-solid-1 px-4 pb-3 pt-4"
    >
      <div class="flex items-center justify-between">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-chevron-left"
          class="!rounded-full bg-n-alpha-black2"
          @click="emit('back')"
        />
        <div class="flex shrink-0 items-center gap-1.5">
          <Button
            v-for="action in PRIMARY_ACTIONS"
            :key="action.key"
            variant="ghost"
            color="slate"
            size="sm"
            :icon="action.icon"
            class="!rounded-full bg-n-alpha-black2"
            :title="t(action.labelKey)"
            :aria-label="t(action.labelKey)"
            @click="action.handler"
          />
          <div class="relative">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-ellipsis"
              class="!rounded-full bg-n-alpha-black2"
              :title="t('CLOSE_CIRCLE.ACTIONS.MORE')"
              :aria-label="t('CLOSE_CIRCLE.ACTIONS.MORE')"
              @click="showActionsMenu = !showActionsMenu"
            />
            <div
              v-if="showActionsMenu"
              class="absolute right-0 top-full z-20 mt-1 min-w-[220px] rounded-xl border border-n-strong bg-n-solid-1 py-1 shadow-lg"
            >
              <button
                v-for="action in MENU_ACTIONS"
                :key="action.key"
                type="button"
                class="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-n-alpha-1"
                :class="
                  action.color === 'ruby' ? 'text-n-ruby-11' : 'text-n-slate-12'
                "
                @click="action.handler"
              >
                <span :class="action.icon" class="size-4 shrink-0" />
                {{ t(action.labelKey) }}
              </button>
            </div>
            <div
              v-if="showActionsMenu"
              class="fixed inset-0 z-10"
              @click="showActionsMenu = false"
            />
          </div>
        </div>
      </div>
      <div class="min-w-0">
        <h2 class="truncate text-lg font-bold text-n-slate-12">
          {{ memberName }}
        </h2>
        <p class="text-sm text-n-slate-11">{{ relationshipLabel }}</p>
      </div>
    </div>

    <div
      class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-n-alpha-1 py-2"
    >
      <section
        v-if="hasInformation"
        class="flex flex-col gap-1.5 bg-n-solid-1 px-4 py-3 text-sm text-n-slate-11"
      >
        <p v-if="member.phone" class="flex items-center gap-2">
          <span class="i-lucide-phone size-3.5 text-n-slate-10" />
          {{ member.phone }}
        </p>
        <p v-if="member.email" class="flex items-center gap-2">
          <span class="i-lucide-mail size-3.5 text-n-slate-10" />
          {{ member.email }}
        </p>
        <p v-if="preferredLanguageName" class="flex items-center gap-2">
          <span class="i-lucide-languages size-3.5 text-n-slate-10" />
          {{
            t('CLOSE_CIRCLE.DETAIL.PREFERRED_LANGUAGE', {
              language: preferredLanguageName,
            })
          }}
        </p>
        <p v-if="member.canBeContactedDirectly" class="flex items-center gap-2">
          <span class="i-lucide-check size-3.5 text-n-teal-10" />
          {{ t('CLOSE_CIRCLE.FORM.DIRECT_CONTACT_ALLOWED') }}
        </p>
      </section>

      <section
        v-if="hasPersonalMarkers"
        class="flex flex-col gap-3 bg-n-solid-1 px-4 py-3"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('CLOSE_CIRCLE.DETAIL.KEY_CONTEXT') }}
        </h3>

        <div v-if="importantDateRows.length" class="flex flex-col gap-1">
          <div
            v-for="date in importantDateRows"
            :key="date.id"
            class="flex items-center justify-between gap-3 py-1.5 text-sm"
          >
            <span class="flex min-w-0 items-center gap-2">
              <span class="i-lucide-calendar-days size-3.5 text-n-slate-10" />
              <span class="truncate text-n-slate-12">
                {{ date.label || date.typeLabel }}
              </span>
            </span>
            <span class="shrink-0 text-n-slate-11">
              {{ date.formattedDate }}
            </span>
          </div>
        </div>

        <div v-if="preferenceRows.length" class="flex flex-col gap-1.5">
          <div
            v-for="row in preferenceRows"
            :key="row.label"
            class="flex items-start justify-between gap-3 text-sm"
          >
            <span class="shrink-0 text-n-slate-10">{{ row.label }}</span>
            <span class="min-w-0 text-right text-n-slate-11">{{
              row.value
            }}</span>
          </div>
        </div>

        <div v-if="member.notes" class="flex items-start gap-2 text-sm">
          <span
            class="i-lucide-message-square-text mt-0.5 size-3.5 text-n-slate-10"
          />
          <p class="text-sm leading-relaxed text-n-slate-11">
            {{ member.notes }}
          </p>
        </div>
      </section>

      <CloseCircleLinkedProjects
        :member="member"
        :projects="projects"
        @open-project="project => emit('openProject', project)"
      />

      <LinkedTasksPreview
        :contact="contact"
        :contact-id="contactId"
        :close-circle-member-id="member.id"
        :close-circle-member-name="memberName"
        :title="t('CLOSE_CIRCLE.DETAIL.LINKED_TASKS')"
        :flush-on-desktop="false"
        class="!mt-0"
      />
    </div>

    <TaskForm ref="taskFormRef" />
  </div>
</template>
